import React, { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { authAPI } from '../services/api';
import { useAuth } from './AuthContext';

export interface Company {
  id: string;
  name: string;
  document?: string;
  email?: string;
  is_active: boolean;
}

interface CompanyContextType {
  currentCompany: Company | null;
  companies: Company[];
  isLoading: boolean;
  setCurrentCompany: (company: Company) => Promise<void>;
  loadCompanies: () => Promise<void>;
  refreshCompany: () => Promise<void>;
}

const defaultCompanyContext: CompanyContextType = {
  currentCompany: null,
  companies: [],
  isLoading: false,
  setCurrentCompany: async () => undefined,
  loadCompanies: async () => undefined,
  refreshCompany: async () => undefined,
};

const CompanyContext = createContext<CompanyContextType>(defaultCompanyContext);

export const CompanyProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated, token } = useAuth();
  const [currentCompany, setCurrentCompanyState] = useState<Company | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Carregar empresas do usuário
  const loadCompanies = async () => {
    try {
      setIsLoading(true);
      const response = await authAPI.get('/companies');
      
      console.log('[CompanyContext] Raw response:', response.data);
      
      // Garantir que sempre temos um array
      let companiesData: Company[] = [];
      if (Array.isArray(response.data)) {
        companiesData = response.data;
      } else if (response.data && typeof response.data === 'object') {
        // Se vier como objeto, tentar extrair o array
        companiesData = response.data.companies || response.data.data || response.data.results || [];
      }
      
      // Garantir que é um array válido
      if (!Array.isArray(companiesData)) {
        console.warn('[CompanyContext] Companies data is not an array:', companiesData);
        companiesData = [];
      }
      
      console.log('[CompanyContext] Loaded companies:', companiesData.length, companiesData);
      setCompanies(companiesData);
      
      // Se não há empresa atual selecionada, usar a primeira disponível
      if (!currentCompany && companiesData.length > 0) {
        const savedCompanyId = localStorage.getItem('current_company_id');
        const companyToUse = savedCompanyId 
          ? companiesData.find(c => c.id === savedCompanyId) || companiesData[0]
          : companiesData[0];
        
        console.log('[CompanyContext] Setting current company:', companyToUse);
        setCurrentCompanyState(companyToUse);
        localStorage.setItem('current_company_id', companyToUse.id);
      } else if (companiesData.length === 0) {
        console.warn('[CompanyContext] No companies found for user');
      }
    } catch (error: any) {
      console.error('[CompanyContext] Failed to load companies:', error);
      console.error('[CompanyContext] Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      setCompanies([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Definir empresa atual
  const setCurrentCompany = async (company: Company) => {
    try {
      // Se a empresa já é a atual, não fazer nada
      if (currentCompany?.id === company.id) {
        return;
      }

      // Chamar endpoint para trocar empresa e obter novos tokens
      try {
        const response = await authAPI.post('/auth/switch-company', {
          company_id: company.id,
        });

        // Atualizar tokens no localStorage
        if (response.data.token) {
          localStorage.setItem('token', response.data.token);
          if (response.data.refresh_token) {
            localStorage.setItem('refresh_token', response.data.refresh_token);
          }
          
          // Atualizar token em todos os interceptors do axios
          // Os interceptors já pegam do localStorage, então não precisa fazer nada mais
        }

        // Atualizar estado
        setCurrentCompanyState(company);
        localStorage.setItem('current_company_id', company.id);
        
        console.info('[CompanyContext] Empresa alterada para:', company.name);
      } catch (switchError: any) {
        // Se falhar ao trocar empresa, apenas atualizar o contexto local
        // Isso permite que o usuário continue usando a UI mesmo se o backend falhar
        console.warn('[CompanyContext] Failed to switch company via API, updating local context only:', switchError);
        setCurrentCompanyState(company);
        localStorage.setItem('current_company_id', company.id);
        
        // Se for erro 401 ou 403, pode ser que o usuário não tenha acesso
        if (switchError.response?.status === 403 || switchError.response?.status === 401) {
          throw new Error('Você não tem acesso a esta empresa');
        }
      }
    } catch (error) {
      console.error('[CompanyContext] Failed to set company:', error);
      throw error;
    }
  };

  // Recarregar empresa atual
  const refreshCompany = async () => {
    if (!currentCompany) return;
    
    try {
      const response = await authAPI.get<Company>(`/companies/${currentCompany.id}`);
      setCurrentCompanyState(response.data);
    } catch (error) {
      console.error('[CompanyContext] Failed to refresh company:', error);
    }
  };

  // Carregar empresas quando autenticado
  useEffect(() => {
    if (isAuthenticated && token) {
      // Adicionar um delay maior para garantir que o token está totalmente propagado
      // e que o backend reconheceu o token antes de fazer requisições
      // Isso evita race conditions onde requisições são feitas antes do token estar disponível
      const timer = setTimeout(() => {
        // Verificar novamente se o token ainda está disponível antes de fazer requisição
        const currentToken = localStorage.getItem('token');
        if (currentToken) {
          loadCompanies();
        } else {
          console.warn('[CompanyContext] Token não encontrado no localStorage, pulando carregamento de empresas');
        }
      }, 300); // Aumentado de 100ms para 300ms
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, token]);

  // Restaurar empresa salva ao montar
  useEffect(() => {
    const savedCompanyId = localStorage.getItem('current_company_id');
    if (savedCompanyId && companies.length > 0) {
      const savedCompany = companies.find(c => c.id === savedCompanyId);
      if (savedCompany && savedCompany.id !== currentCompany?.id) {
        setCurrentCompanyState(savedCompany);
      }
    }
  }, [companies]);

  const value: CompanyContextType = {
    currentCompany,
    companies,
    isLoading,
    setCurrentCompany,
    loadCompanies,
    refreshCompany,
  };

  return <CompanyContext.Provider value={value}>{children}</CompanyContext.Provider>;
};

export const useCompany = (): CompanyContextType => {
  return useContext(CompanyContext);
};

